import React, { useEffect, useRef, useState } from 'react';
import { AuthAPI, AuthConfig } from '../../api/client';

/**
 * Shared building blocks for the custom auth screens that replace the Cognito
 * Hosted UI. Styling mirrors ProfileGate.tsx so the auth flow feels native.
 */

// --- Auth config (mode / CAPTCHA site key / password policy) -----------------
// Fetched once and cached module-side; every auth page reads it.
let _cache: AuthConfig | null = null;

// Used when /auth/config can't be reached (e.g. transient backend blip). We
// assume the real cognito flow and no CAPTCHA rather than hanging the login
// screen on "Loading…" — the form still works; CAPTCHA (if required) is enforced
// server-side regardless.
const FALLBACK_CONFIG: AuthConfig = {
  mode: 'cognito',
  captchaSiteKey: '',
  passwordPolicy: { minLength: 12, requireUppercase: true, requireLowercase: true, requireNumbers: true, requireSymbols: true },
};

// A single shared fetch, deduped module-side. Guarding setCfg with an `active`
// cleanup flag (the usual pattern) is WRONG here: React StrictMode mounts →
// unmounts → remounts in dev, and the fetch reliably resolves after the first
// cleanup, so the update gets dropped and the screen hangs on "Loading…". React
// 18 makes setState on an unmounted component a harmless no-op, so we just set
// state on resolve; the live instance updates, dropped ones no-op.
let _inflight: Promise<AuthConfig> | null = null;

function loadAuthConfig(): Promise<AuthConfig> {
  if (_cache) return Promise.resolve(_cache);
  if (!_inflight) {
    _inflight = AuthAPI.config()
      .then(c => { _cache = c || FALLBACK_CONFIG; return _cache; })
      .catch(() => { _inflight = null; return FALLBACK_CONFIG; });  // allow retry after a failure
  }
  return _inflight;
}

export function useAuthConfig(): AuthConfig | null {
  const [cfg, setCfg] = useState<AuthConfig | null>(_cache);
  useEffect(() => {
    if (cfg) return;
    loadAuthConfig().then(setCfg);
  }, [cfg]);
  return cfg;
}

// --- Layout ------------------------------------------------------------------

export function AuthLayout({ title, subtitle, children }: {
  title: string; subtitle?: string; children: React.ReactNode;
}) {
  return (
    <div style={overlay}>
      <div style={card}>
        <h1 style={{ fontSize: '1.25rem', margin: '0 0 .25rem' }}>{title}</h1>
        {subtitle && <p style={{ color: '#64748b', margin: '0 0 1.25rem', fontSize: '.9rem' }}>{subtitle}</p>}
        {children}
        <p style={authFooter}>
          <a href="https://www.accenture.com/en/support/privacy-policy"
             target="_blank" rel="noopener noreferrer" style={authFooterLink}>
            Privacy Statement
          </a>
        </p>
      </div>
    </div>
  );
}

const authFooter: React.CSSProperties = {
  marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0',
  fontSize: '.75rem', color: '#94a3b8', textAlign: 'center',
};
const authFooterLink: React.CSSProperties = {
  color: '#64748b', textDecoration: 'none', fontWeight: 600,
};

export function ErrorText({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return <p style={{ color: '#dc2626', fontSize: '.85rem', margin: '0 0 .75rem' }}>{children}</p>;
}

export function InfoText({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return <p style={{ color: '#0369a1', fontSize: '.85rem', margin: '0 0 .75rem' }}>{children}</p>;
}

// --- Password policy hints ---------------------------------------------------

export function passwordMeetsPolicy(pw: string, policy?: AuthConfig['passwordPolicy']): boolean {
  const p = policy || { minLength: 12, requireUppercase: true, requireLowercase: true, requireNumbers: true, requireSymbols: true };
  if (pw.length < p.minLength) return false;
  if (p.requireUppercase && !/[A-Z]/.test(pw)) return false;
  if (p.requireLowercase && !/[a-z]/.test(pw)) return false;
  if (p.requireNumbers && !/[0-9]/.test(pw)) return false;
  if (p.requireSymbols && !/[^A-Za-z0-9]/.test(pw)) return false;
  return true;
}

export function PasswordHints({ policy }: { policy?: AuthConfig['passwordPolicy'] }) {
  const p = policy || { minLength: 12, requireUppercase: true, requireLowercase: true, requireNumbers: true, requireSymbols: true };
  const parts = [`at least ${p.minLength} characters`];
  if (p.requireUppercase) parts.push('an uppercase letter');
  if (p.requireLowercase) parts.push('a lowercase letter');
  if (p.requireNumbers) parts.push('a number');
  if (p.requireSymbols) parts.push('a symbol');
  return (
    <p style={{ color: '#64748b', fontSize: '.75rem', margin: '-.35rem 0 .9rem' }}>
      Password must include {parts.join(', ')}.
    </p>
  );
}

// --- Cloudflare Turnstile CAPTCHA --------------------------------------------
// Renders the widget when a site key is configured; otherwise renders nothing
// and reports an empty token (backend treats CAPTCHA as disabled locally).

declare global {
  interface Window { turnstile?: any; }
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (t: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!siteKey) { onToken(''); return; }
    let widgetId: string | undefined;
    let cancelled = false;

    const render = () => {
      if (cancelled || !ref.current || !window.turnstile) return;
      widgetId = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    };

    if (window.turnstile) {
      render();
    } else if (!document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
      const s = document.createElement('script');
      s.src = TURNSTILE_SRC; s.async = true; s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      const t = setInterval(() => { if (window.turnstile) { clearInterval(t); render(); } }, 200);
      return () => clearInterval(t);
    }
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) { try { window.turnstile.remove(widgetId); } catch { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} style={{ margin: '0 0 .9rem' }} />;
}

// --- Shared styles (mirror ProfileGate.tsx) ----------------------------------

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: '#0f172a',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  fontFamily: 'system-ui, sans-serif',
};
const card: React.CSSProperties = {
  background: '#fff', padding: '2rem 2.25rem', borderRadius: 12, width: 360,
  boxShadow: '0 10px 40px rgba(0,0,0,.2)',
};
export const label: React.CSSProperties = {
  display: 'block', fontSize: '.8rem', fontWeight: 600, color: '#334155', marginBottom: '.75rem',
};
export const input: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: '.35rem', padding: '.6rem .75rem',
  border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '.95rem', boxSizing: 'border-box',
};
export const button: React.CSSProperties = {
  width: '100%', marginTop: '.5rem', padding: '.7rem', background: '#3b82f6', color: '#fff',
  border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '.95rem', cursor: 'pointer',
};
export const linkRow: React.CSSProperties = {
  marginTop: '1rem', fontSize: '.85rem', color: '#64748b', textAlign: 'center',
};
export const linkStyle: React.CSSProperties = {
  color: '#3b82f6', textDecoration: 'none', fontWeight: 600, cursor: 'pointer',
};
